// Global Types
import type {
  $GeoLocation,
} from 'common.types';
import type {
  $Model,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Marker,
} from '@react-google-maps/api';

// Helpers
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getImageUrl,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  formatDateRange,
  getPreviousRegionSeasonId,
  getRegionByRegionSeasonId,
  getRegionSeasonByRegionSeasonId,
  isLatestRegionSeason,
} from 'common.helpers/dist/browser';
import {
  regattaStatusLabel,
} from 'common.language';
import {
  getLocalItemPath,
} from '../../../../helpers';
import config from '../../../../config';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import Map from '../../../common/Map';
import ShareButton from '../../../common/ShareButton';

// Enums
import {
  statusLabelClass,
} from '../../../../enums';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  item: $Model<'Regatta'>;
};

export type $StateProps = {
  auth: $ReducerState<'auth'>;
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  regattaSubscription: $Model<'RegattaSubscription'> | null;
};

class DetailsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      regattaSubscription: null,
    };
  }

  componentDidMount(): void {
    const {
      auth,
    } = this.props;

    if (auth.user !== null) {
      this.getData();
    }

    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      auth,
    } = this.props;

    if (auth.user !== null && isDiff(
      prevProps.auth.user,
      auth.user,
    )) {
      this.getData();
    }
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    try {
      const regattaSubscription = await Services.Api.getRegattaSubscriptionData({
        RegattaId: item.id,
      });

      await this.setStateAsync({
        regattaSubscription,
      });
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  setupSocket(): void {
    const {
      auth,
      item,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        if (auth.user !== null && data.RegattaId === item.id && data.parentModelId === auth.user.id && data.modelName === 'RegattaSubscription') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  async toggleRegattaSubscription(): Promise<void> {
    const {
      item,
    } = this.props;

    try {
      Services.Api.toggleRegattaSubscriptionData({
        RegattaId: item.id,
      });
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  renderLogo() {
    const {
      item,
      t,
    } = this.props;

    const imageUrl: string = getImageUrl({
      cdnUrl: config.CDN_URL,
      image: item.Image,
      size: 'max',
    });

    return (item.Image ? (
      <div className={'hidden md:block  mr-5'}>
        <img
          alt={t('Logo')}
          className={'max-h-32 grow'}
          src={imageUrl}
        />
      </div>
    ) : null);
  }

  renderTitle() {
    const {
      item,
      t,
    } = this.props;

    const imageUrl: string = getImageUrl({
      cdnUrl: config.CDN_URL,
      image: item.Image,
      size: 'mid',
    });

    return (
      <div className={'flex flex-row items-center'}>
        <div className={'md:hidden'}>
          {item.Image && (
            <img
              alt={t('Logo')}
              className={'flex-none w-20 mr-3'}
              src={imageUrl}
            />
          )}
        </div>
        <div className={'flex-1'}>
          <h1
            className={'leading-none'}
            title={t('Regatta title')}
          >
            <span className={'md:hidden h3'}>
              {item.Name}
            </span>
            <span className={'hidden md:block h1'}>
              {item.Name}
            </span>
          </h1>
        </div>
      </div>
    );
  }

  renderStatus() {
    const {
      item,
      t,
    } = this.props;

    return (
      <div className={`badge badge_outlined badge_${statusLabelClass[item.Status]} uppercase py-1 mr-3`}>
        {t(regattaStatusLabel[item.Status])}
      </div>
    );
  }

  renderDate() {
    const {
      item,
      main,
      t,
    } = this.props;

    const {
      RegionSeasonId,
    } = item;

    const region = getRegionByRegionSeasonId({
      regions: main.regions,
      RegionSeasonId,
    });

    const regionSeason = getRegionSeasonByRegionSeasonId({
      regions: main.regions,
      RegionSeasonId,
    });

    const date: string = formatDateRange({
      end: item.EndDate,
      shortDateFormat: region.ShortDateFormat,
      start: item.StartDate,
    });

    return (
      <div className={'text-md sm:text-lg mr-3'}>
        {!isLatestRegionSeason({
          regions: main.regions,
          RegionSeasonId: item.RegionSeasonId,
        }) && (
          <span>
            {regionSeason.Name}
            <span className={'text-gray-400'}>
              {' | '}
            </span>
          </span>
        )}
        <span title={t('Regatta date')}>
          {date}
        </span>
      </div>
    );
  }

  renderOrganizer() {
    const {
      item,
    } = this.props;

    return (item.OrganizerName ? (
      <div className={'mb-3'}>
        <a
          className={'text-gray-600'}
          href={`mailto:${item.OrganizerName || ''}<${item.OrganizerEmail}>?subject=${item.Name}`}
        >
          <span className={'break-all'}>{item.OrganizerName || ''}</span>
        </a>
      </div>
    ) : null);
  }

  renderData() {
    const {
      item,
      t,
    } = this.props;

    return (
      <div className={'grow grid content-between'}>
        <div>
          <div className={'mb-3'}>
            {this.renderTitle()}
          </div>
          {this.renderOrganizer()}
        </div>
        <div className={'flex flex-row items-center'}>
          {this.renderStatus()}
          {this.renderDate()}
          <div
            className={'text-md sm:text-lg font-bold'}
            title={t('Regatta location')}
          >
            {item.Location}
          </div>
        </div>
      </div>
    );
  }

  renderButtons() {
    const {
      auth,
    } = this.props;
    const {
      regattaSubscription,
    } = this.state;

    return (
      <div className={'ml-2 sm:ml-5 flex flex-col justify-between'}>
        <div className={'flex flex-col md:flex-row'}>
          <button
            className={`btn btn_primary ${regattaSubscription ? 'btn_active' : 'btn_outlined'} uppercase px-2 py-1 mb-3 ml-auto md:mr-3`}
            disabled={auth.user === null}
            onClick={() => this.toggleRegattaSubscription()}
            type={'button'}
          >
            <em className={'la la-bell text-2xl'} />
          </button>
          <div className={'mb-3 ml-auto md:ml-0'}>
            <ShareButton />
          </div>
        </div>
        {this.renderReferenceButton()}
      </div>
    );
  }

  renderReferenceButton() {
    const {
      item,
      main,
      t,
    } = this.props;

    const id = item.ReferenceId;

    if (id) {
      const previousRegionSeasonId = getPreviousRegionSeasonId({
        regions: main.regions,
        RegionSeasonId: item.RegionSeasonId,
      });

      if (previousRegionSeasonId !== null) {
        const itemUrl = getLocalItemPath({
          basePath: '/regatta',
          item: {
            id,
            RegionSeasonId: previousRegionSeasonId,
          },
          regions: main.regions,
        });

        return (
          <a
            className={'btn btn_primary btn_outlined uppercase px-2 py-1'}
            href={itemUrl}
            title={t('Previous year regatta')}
          >
            <em className={'la la-history text-2xl'} />
            <span className={'hidden md:block ml-2'}>
              {t('Previous year regatta')}
            </span>
          </a>
        );
      }
    }

    return null;
  }

  renderMap() {
    const {
      item,
    } = this.props;

    if (item.GeoLocationLat && item.GeoLocationLng) {
      const location: $GeoLocation = {
        lat: item.GeoLocationLat,
        lng: item.GeoLocationLng,
      };

      return (
        <div className={'overflow-hidden rounded-bl-lg rounded-br-lg -mx-5 -mb-5 mt-5'}>
          <Map
            center={location}
            controls
            draggable={false}
            height={150}
            zoom={14}
          >
            <Marker
              icon={{
                scaledSize: {
                  equals: undefined,
                  height: 42,
                  width: 42,
                },
                url: `${config.CDN_URL}/static/map/marker-active.png`,
              }}
              label={{
                className: 'marker-label',
                text: item.GeoLocation,
              }}
              position={location}
            />
          </Map>
        </div>
      );
    }

    return null;
  }

  render() {
    return (
      <Section id={webSections[webPages.regattaPage].details}>
        <>
          <div className={'flex flex-row'}>
            {this.renderLogo()}
            {this.renderData()}
            {this.renderButtons()}
          </div>
          {this.renderMap()}
        </>
      </Section>
    );
  }
}

export default DetailsSectionView;
